import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import Layout from "../components/Layout";
import { useConfig } from "../lib/config";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, School } from "lucide-react";

const EMPTY = { id: null, nombre: "", codigo: "", facultad_id: "", contacto_coordinador: "" };

export default function Unidades() {
  const { t } = useTranslation();
  const { config } = useConfig();
  const [unidades, setUnidades] = useState([]);
  const [facultades, setFacultades] = useState([]);
  const [convStats, setConvStats] = useState({});
  const [userStats, setUserStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const facultadSingular = config.facultad_label_singular || "Facultad";
  const unidadSingular = config.unidad_label_singular || "Unidad";
  const unidadPlural = config.unidad_label_plural || t("uni.default_plural");
  const facultadPlural = config.facultad_label_plural?.toLowerCase() || t("uni.default_plural");

  const load = () => {
    Promise.all([
      api.get("/unidades"),
      api.get("/facultades"),
      api.get("/convenios"),
      api.get("/users"),
    ])
      .then(([u, f, c, us]) => {
        setUnidades(u.data);
        setFacultades(f.data);
        const cs = {};
        c.data.forEach((conv) => (conv.unidad_ids || []).forEach((uid) => (cs[uid] = (cs[uid] || 0) + 1)));
        setConvStats(cs);
        const ustat = {};
        us.data.forEach((usr) => { if (usr.unidad_id) ustat[usr.unidad_id] = (ustat[usr.unidad_id] || 0) + 1; });
        setUserStats(ustat);
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openCreate = () => { setForm(EMPTY); setShowModal(true); };
  const openEdit = (u) => {
    setForm({ id: u.id, nombre: u.nombre, codigo: u.codigo, facultad_id: u.facultad_id || "", contacto_coordinador: u.contacto_coordinador || "" });
    setShowModal(true);
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        nombre: form.nombre,
        codigo: form.codigo,
        facultad_id: form.facultad_id || null,
        contacto_coordinador: form.contacto_coordinador || null,
      };
      if (form.id) await api.patch(`/unidades/${form.id}`, payload);
      else await api.post("/unidades", payload);
      toast.success(form.id ? t("uni.toast_updated") : t("uni.toast_created"));
      setShowModal(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("toast.error_save"));
    } finally { setSaving(false); }
  };

  const remove = async (u) => {
    const users = userStats[u.id] || 0;
    const convs = convStats[u.id] || 0;
    if (users > 0 || convs > 0) return toast.error(t("uni.cannot_delete", { users, convs }));
    if (!confirm(t("uni.confirm_delete", { name: u.nombre }))) return;
    try {
      await api.delete(`/unidades/${u.id}`);
      toast.success(t("uni.toast_deleted"));
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || t("toast.error_generic")); }
  };

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-8 sm:py-12">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 sm:mb-10">
          <div>
            <div className="section-marker">{t("common.administration")}</div>
            <h1 className="text-4xl sm:text-5xl font-semibold tracking-tighter mt-2">
              {config.unidad_label_plural || t("uni.default_plural")}<span className="serif-em text-zinc-900">.</span>
            </h1>
            <p className="mt-3 text-zinc-600 max-w-2xl">
              {t("uni.desc", {
                plural: facultadPlural,
                unidad_plural: unidadPlural.toLowerCase(),
              })}
            </p>
          </div>
          <button onClick={openCreate} className="bg-zinc-950 text-white px-5 py-3 font-medium hover:bg-zinc-800 inline-flex items-center justify-center gap-2 w-full sm:w-auto shrink-0" data-testid="btn-new-unidad">
            <Plus size={18} strokeWidth={1.5} /> {t("uni.btn_new", { singular: unidadSingular.toLowerCase() })}
          </button>
        </div>

        {loading ? (
          <div className="text-zinc-500">{t("common.loading")}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="unidades-grid">
            {unidades.map((u) => {
              const users = userStats[u.id] || 0;
              const convs = convStats[u.id] || 0;
              return (
                <div key={u.id} className="border border-zinc-200 p-6 flex flex-col" data-testid={`uni-${u.id}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="section-marker">{u.codigo}</div>
                    <div className="flex gap-1">
                      <button onClick={() => openEdit(u)} className="text-zinc-400 hover:text-zinc-950 p-1" data-testid={`uni-edit-${u.id}`}>
                        <Pencil size={14} strokeWidth={1.5} />
                      </button>
                      <button onClick={() => remove(u)} className="text-zinc-400 hover:text-red-700 p-1 disabled:opacity-20" disabled={users > 0 || convs > 0} data-testid={`uni-del-${u.id}`}>
                        <Trash2 size={14} strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                  <h3 className="text-xl font-medium tracking-tight mb-2 flex-1">{u.nombre}</h3>
                  {u.facultad_nombre && <div className="text-xs text-zinc-500 mb-4">{t("uni.facultad_prefix", { facultad: facultadSingular })} {u.facultad_nombre}</div>}
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-zinc-100">
                    <div><div className="kpi-number text-2xl">{users}</div><div className="section-marker mt-1">{t("uni.users")}</div></div>
                    <div><div className="kpi-number text-2xl">{convs}</div><div className="section-marker mt-1">{t("uni.convenios")}</div></div>
                  </div>
                </div>
              );
            })}
            {unidades.length === 0 && (
              <div className="col-span-full text-center py-16 border border-zinc-200">
                <School size={36} strokeWidth={1} className="mx-auto text-zinc-400 mb-3" />
                <p className="text-zinc-500">{t("uni.no_data", { plural: unidadPlural.toLowerCase() })}</p>
              </div>
            )}
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
            <div className="bg-white w-full max-w-lg p-6 sm:p-8 border border-zinc-200 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="uni-modal">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="section-marker">{form.id ? t("uni.modal_edit") : t("uni.modal_new")}</div>
                  <h2 className="text-3xl font-medium tracking-tight mt-1">
                    {form.id ? t("uni.update_title", { singular: unidadSingular.toLowerCase() }) : t("uni.create_title", { singular: unidadSingular.toLowerCase() })}
                  </h2>
                </div>
                <button onClick={() => setShowModal(false)} className="text-zinc-400 hover:text-zinc-950"><X size={20} strokeWidth={1.5} /></button>
              </div>
              <form onSubmit={submit} className="space-y-4" data-testid="uni-form">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">{t("uni.field_nombre")}</label>
                  <input required value={form.nombre} onChange={set("nombre")} className="w-full border border-zinc-300 px-4 py-3" data-testid="uni-nombre" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">{t("uni.field_codigo")}</label>
                  <input required value={form.codigo} onChange={set("codigo")} className="w-full border border-zinc-300 px-4 py-3" data-testid="uni-codigo" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                    {t("uni.field_facultad", { facultad: facultadSingular })}
                  </label>
                  <select value={form.facultad_id} onChange={set("facultad_id")} className="w-full border border-zinc-300 px-4 py-3 bg-white" data-testid="uni-facultad">
                    <option value="">{t("uni.field_facultad_none", { facultad: facultadSingular.toLowerCase() })}</option>
                    {facultades.map((f) => (
                      <option key={f.id} value={f.id}>{f.nombre}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">{t("uni.field_contacto")}</label>
                  <input value={form.contacto_coordinador} onChange={set("contacto_coordinador")} className="w-full border border-zinc-300 px-4 py-3" data-testid="uni-contacto" />
                </div>
                <div className="flex gap-3 pt-3">
                  <button type="button" onClick={() => setShowModal(false)} className="flex-1 border border-zinc-300 px-6 py-3 font-medium hover:bg-zinc-50">{t("common.cancel")}</button>
                  <button type="submit" disabled={saving} className="flex-1 bg-zinc-950 text-white px-6 py-3 font-medium hover:bg-zinc-800 disabled:opacity-50" data-testid="uni-submit">
                    {saving ? t("uni.saving") : form.id ? t("uni.update") : t("uni.create")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
